import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { GridCellModes, GridCellModesModel, GridCellParams } from '@mui/x-data-grid-pro';

import StyledDataGrid from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/StyledDataGrid';

import {
  useGetCabinUpgradePricing,
  useUpsertCabinUpgrades,
} from '~/hooks/apis/flights-charter/useCharterUpgradePricing';

import type { CabinUpgradeEligibilityUpsertItem, CabinUpgradePricingUpsertItem } from '~/services/CharterService';

import {
  EDITABLE_ELIGIBILITY_FIELDS,
  EDITABLE_PRICING_FIELDS,
  charterUpgradeColumnGroupingModel,
  charterUpgradeColumns,
} from './CharterUpgradePricingColumns';
import CharterUpgradePricingFilters from './CharterUpgradePricingFilters';
import {
  type UpgradePricingFilters,
  buildEligibilityPayload,
  buildPricingPayload,
  extractEligibilityChange,
  extractPricingChanges,
  pivotToFlightRows,
} from './CharterUpgradePricingHelpers';
import { type FlightUpgradeRow } from './CharterUpgradePricingTypes';

const ALL_EDITABLE_FIELDS = [...EDITABLE_ELIGIBILITY_FIELDS, ...EDITABLE_PRICING_FIELDS];
const editableCellSx = ALL_EDITABLE_FIELDS.map((f) => `& .MuiDataGrid-cell[data-field="${f}"]`).join(', ');

// Page

export default function CharterUpgradePricingPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [appliedFilters, setUpgradePricingFilters] = useState<UpgradePricingFilters>({});
  const [isViewMode, setIsViewMode] = useState(true);
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  // Separate tracking for eligibility (per-flight) and pricing (global)
  const [editedEligibility, setEditedEligibility] = useState<Map<string, CabinUpgradeEligibilityUpsertItem>>(new Map());
  const [editedPricing, setEditedPricing] = useState<Map<string, CabinUpgradePricingUpsertItem>>(new Map());
  // Local row overrides for display (merged eligibility + pricing edits)
  const [editedRows, setEditedRows] = useState<Map<string, FlightUpgradeRow>>(new Map());

  const hasUnsavedChanges = editedEligibility.size > 0 || editedPricing.size > 0;

  const pricingQuery = useGetCabinUpgradePricing({ ...appliedFilters, limit: 1000 });
  const upsertCabinUpgradesMutation = useUpsertCabinUpgrades();

  const baseRows = useMemo<FlightUpgradeRow[]>(() => {
    return pivotToFlightRows(pricingQuery.data?.result ?? []);
  }, [pricingQuery.data]);

  const rows = useMemo(() => baseRows.map((base) => editedRows.get(base.id) ?? base), [baseRows, editedRows]);

  const handleSearch = useCallback((filters: UpgradePricingFilters) => {
    setEditedEligibility(new Map());
    setEditedPricing(new Map());
    setEditedRows(new Map());
    setUpgradePricingFilters(filters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setEditedEligibility(new Map());
    setEditedPricing(new Map());
    setEditedRows(new Map());
    setUpgradePricingFilters({});
  }, []);

  const processRowUpdate = useCallback((newRow: FlightUpgradeRow, oldRow: FlightUpgradeRow): FlightUpgradeRow => {
    // Track eligibility changes
    const eligibilityChange = extractEligibilityChange(newRow, oldRow);
    if (eligibilityChange) {
      setEditedEligibility((prev) => new Map(prev).set(newRow.id, eligibilityChange));
    }

    // Track pricing changes
    const pricingChanges = extractPricingChanges(newRow, oldRow);
    if (pricingChanges.size > 0) {
      setEditedPricing((prev) => {
        const updated = new Map(prev);
        pricingChanges.forEach((item, key) => updated.set(`${item.eligibilityId}_${key}`, item));
        return updated;
      });
    }

    // Update display row
    setEditedRows((prev) => new Map(prev).set(newRow.id, newRow));
    return newRow;
  }, []);

  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (isViewMode || !params.isEditable) return;

      if ((event.target as HTMLElement).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
        return;
      }

      setCellModesModel((prev) => ({
        ...Object.keys(prev).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prev[id]).reduce(
              (acc2, field) => ({ ...acc2, [field]: { mode: GridCellModes.View } }),
              {},
            ),
          }),
          {},
        ),
        [params.id]: {
          ...Object.keys(prev[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {},
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      }));
    },
    [isViewMode],
  );

  const handleSave = useCallback(async () => {
    if (editedEligibility.size === 0 && editedPricing.size === 0) {
      enqueueSnackbar('No changes to save', { variant: 'warning' });
      return;
    }

    const eligibilityPayload = buildEligibilityPayload(editedEligibility);
    const pricingPayload = buildPricingPayload(editedPricing);

    try {
      await upsertCabinUpgradesMutation.mutateAsync({ eligibility: eligibilityPayload, pricing: pricingPayload });
      enqueueSnackbar('Changes saved', { variant: 'success' });
      setEditedEligibility(new Map());
      setEditedPricing(new Map());
      setEditedRows(new Map());
      setCellModesModel({});
      setIsViewMode(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      enqueueSnackbar(`Failed to save: ${message}`, { variant: 'error' });
    }
  }, [editedEligibility, editedPricing, upsertCabinUpgradesMutation, enqueueSnackbar]);

  const handleExitEditMode = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsDiscardDialogOpen(true);
    } else {
      setIsViewMode(true);
    }
  }, [hasUnsavedChanges]);

  const handleConfirmDiscard = useCallback(() => {
    setEditedEligibility(new Map());
    setEditedPricing(new Map());
    setEditedRows(new Map());
    setCellModesModel({});
    setIsDiscardDialogOpen(false);
    setIsViewMode(true);
  }, []);

  const isSaving = upsertCabinUpgradesMutation.isPending;
  const isLoading = pricingQuery.isFetching;

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Charter Cabin Upgrades
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure day windows (per-flight) and pricing (per flight) for cabin upgrades.
        </Typography>
      </Stack>

      <CharterUpgradePricingFilters isLoading={isLoading} onSearch={handleSearch} onClear={handleClearFilters} />

      {pricingQuery.isError && <Alert severity="error">Failed to load upgrade pricing data</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {rows.length > 0 && `${rows.length} flight${rows.length !== 1 ? 's' : ''} (9 upgrade combos each)`}
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={isViewMode ? () => setIsViewMode(false) : handleExitEditMode}
          >
            {isViewMode ? 'Edit' : 'View Mode'}
          </Button>

          {!isViewMode && (
            <LoadingButton
              variant="contained"
              size="small"
              color="primary"
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
            >
              Save Changes
            </LoadingButton>
          )}
        </Stack>
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && !pricingQuery.isError && rows.length === 0 && (
        <Alert severity="info">
          No flights found. Use the filters above to find flights, then configure upgrade day windows and pricing.
        </Alert>
      )}

      {!isLoading && (pricingQuery.isError || rows.length > 0) && (
        <Box sx={{ width: '100%' }}>
          <StyledDataGrid
            rows={rows}
            columns={charterUpgradeColumns}
            columnGroupingModel={charterUpgradeColumnGroupingModel}
            editMode="cell"
            isCellEditable={() => !isViewMode}
            cellModesModel={cellModesModel}
            onCellModesModelChange={setCellModesModel}
            onCellClick={handleCellClick}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(err) =>
              enqueueSnackbar(err instanceof Error ? err.message : 'Invalid value', {
                variant: 'error',
              })
            }
            disableRowSelectionOnClick
            sx={{
              maxHeight: 800,
              ...(!isViewMode && {
                [`${editableCellSx}`]: {
                  outline: '1px solid',
                  outlineColor: 'grey.400',
                  outlineOffset: -1,
                  borderRadius: 1,
                  '&:hover': { outlineColor: 'primary.main' },
                },
                '& .MuiDataGrid-cell--editing': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                  boxShadow: 1,
                },
              }),
            }}
          />
        </Box>
      )}

      {/* Discard confirmation */}
      <Dialog open={isDiscardDialogOpen} onClose={() => setIsDiscardDialogOpen(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>You have unsaved changes. If you leave Edit Mode, they will be discarded.</DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDiscard} variant="text">
            Discard changes
          </Button>
          <Button onClick={() => setIsDiscardDialogOpen(false)} variant="contained" color="primary">
            Keep editing
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
